import { useCallback, useRef, useState } from "react";
import { Notify } from "@/components/ui/notify";
import { useCards } from "@/features/cards/hooks";
import type { CreatedDeviceResponse } from "@/features/devices/hooks";
import type { Selection } from "@/types/device-manager.types";
import { TreePane } from "./TreePane";
import { DetailPanel } from "./DetailPanel";
import { RevealedDeviceKeyCard } from "./RevealedDeviceKeyCard";
import { useDeviceTree } from "./hooks/useDeviceTree";
import { useTreeSelection } from "./hooks/useTreeSelection";
import { useDebouncedValue } from "./hooks/useDebouncedValue";
import type { TreeCtx } from "./TreeNodes";

export function DeviceManager() {
  const { devices, sensorsByCardId, sensorCountByDeviceCode, isLoading, isError } = useDeviceTree();
  const { sel, expanded, query, setQuery, toggle, select } = useTreeSelection();
  const [creatingClp, setCreatingClp] = useState(false);
  const [createdReveal, setCreatedReveal] = useState<CreatedDeviceResponse | null>(null);

  // Dirty-guard: trocar de seleção com edição não salva pede confirmação.
  const dirtyRef = useRef(false);
  const onDirtyChange = useCallback((d: boolean) => {
    dirtyRef.current = d;
  }, []);
  const guardedSelect = useCallback(
    (next: Selection) => {
      if (!dirtyRef.current) {
        select(next);
        return;
      }
      void Notify.confirm({
        variant: "warning",
        title: "Descartar alterações não salvas?",
        confirmLabel: "Descartar",
      }).then((ok) => {
        if (ok) {
          dirtyRef.current = false;
          select(next);
        }
      });
    },
    [select]
  );

  const clp = devices.find((d) => d.id === sel?.clpId);
  const cardsForSel = useCards(sel?.clpId ?? "", { enabled: Boolean(sel?.clpId) }).data ?? [];
  const card =
    sel && (sel.type === "card" || sel.type === "sensor")
      ? cardsForSel.find((c) => c.id === sel.cardId)
      : undefined;
  const sensor =
    sel?.type === "sensor"
      ? (sensorsByCardId.get(sel.cardId) ?? []).find((s) => s.id === sel.sensorId)
      : undefined;

  // Input imediato (`query`); filtro/abertura da árvore usam o valor debounced.
  const debouncedQuery = useDebouncedValue(query, 200);
  const ctx: TreeCtx = {
    query: debouncedQuery,
    sel,
    expanded,
    sensorsByCardId,
    onToggle: toggle,
    onSelect: guardedSelect,
  };

  function onCreatedClp(result: CreatedDeviceResponse) {
    setCreatingClp(false);
    setCreatedReveal(result);
    dirtyRef.current = false;
    select({ type: "clp", clpId: result.device.id });
  }

  // Pós-exclusão: limpa o dirty (o form excluído não dispara onDirtyChange(false) ao
  // desmontar) e navega SEM o guard — não faz sentido perguntar "descartar" de um item já apagado.
  const onDeleted = useCallback(
    (next: Selection) => {
      dirtyRef.current = false;
      select(next);
    },
    [select]
  );

  return (
    <div className="flex h-[calc(100vh-8.5rem)] flex-col gap-4">
      <header>
        <h1 className="text-[20px] font-semibold text-text-primary">CLPs &amp; Sensores</h1>
        <p className="text-[13px] text-text-secondary">
          Cadastre controladores, cartões de I/O e sensores em uma única árvore — e vincule cada
          sensor a um parâmetro.
        </p>
      </header>

      {createdReveal && (
        <RevealedDeviceKeyCard revealed={createdReveal} onClose={() => setCreatedReveal(null)} />
      )}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(280px,340px)_1fr]">
        <TreePane
          devices={devices}
          sensorCountByDeviceCode={sensorCountByDeviceCode}
          query={query}
          setQuery={setQuery}
          ctx={ctx}
          isLoading={isLoading}
          isError={isError}
          onAddClp={() => {
            dirtyRef.current = false;
            setCreatingClp(true);
            select(null);
          }}
        />
        <div className="min-h-0 overflow-auto rounded-lg border border-border-default bg-card p-5">
          <DetailPanel
            creatingClp={creatingClp}
            onCreatedClp={onCreatedClp}
            onCancelCreate={() => setCreatingClp(false)}
            devicesCount={devices.length}
            sel={sel}
            clp={clp}
            card={card}
            sensor={sensor}
            cards={cardsForSel}
            sensorsByCardId={sensorsByCardId}
            onSelect={guardedSelect}
            onDirtyChange={onDirtyChange}
            onDeleted={onDeleted}
          />
        </div>
      </div>
    </div>
  );
}
